import fuzzysort from 'fuzzysort';
import { useCallback, useEffect, useMemo } from 'react';
import { recordDuration, recordSample } from '@/shared/utils/perf';
import { usePlaylistGridState } from '../stores/playlistGridState';
import type { SearchMode, SortDirection } from '@/lib/types/Media';
import type { PlaylistGridRow, PlaylistSortKey } from '@/lib/types/Playlist';

const GRID_INDEX_CACHE_LIMIT = 3;
const SEARCH_CACHE_LIMIT = 12;

const STATE_SORT_RANK: Record<string, number> = {
  active: 0,
  inactive: 1,
};

interface SortState {
  key: PlaylistSortKey;
  direction: SortDirection;
}

interface SearchState {
  mode: SearchMode;
  text: string;
}

interface UsePlaylistGridEngineArgs {
  customerId: number | null;
  playlists: PlaylistGridRow[];
  sort: SortState;
  search: SearchState;
}

interface TimingStats {
  searchMs: number;
  sortMs: number;
  sortBuildMs: number;
  totalMs: number;
}

interface DatasetRecord {
  id: number;
  source: PlaylistGridRow;
  title: string;
  listType: string;
  state: string;
  created: number;
  updated: number;
  lowerTitle: string | null;
  listTypeSortValue: string | null;
  stateRank: number | null;
}

interface SortOrder {
  asc: number[];
  desc: number[];
}

interface FuzzyPreparedRecord {
  id: number;
  preparedTitle: ReturnType<typeof fuzzysort.prepare>;
}

interface PlaylistGridIndex {
  cacheKey: string;
  signature: string;
  idToPlaylist: Map<number, PlaylistGridRow>;
  sourceOrderIds: number[];
  sourceOrderIsUpdatedDesc: boolean;
  records: DatasetRecord[];
  sortOrders: Partial<Record<PlaylistSortKey, SortOrder>>;
  exactSearchCache: Map<string, Set<number>>;
  fuzzySearchCache: Map<string, Set<number>>;
  fuzzyPreparedRecords: FuzzyPreparedRecord[] | null;
  lastUsedAt: number;
}

interface PlaylistGridIndexResult {
  index: PlaylistGridIndex;
  cacheHit: boolean;
}

interface ComputeResult {
  ids: number[];
  timings: TimingStats;
}

export interface UsePlaylistGridEngineResult {
  count: number;
  orderedIds: number[];
  getIdByIndex: (index: number) => number | undefined;
  getRowByIndex: (index: number) => PlaylistGridRow | null;
  resultCount: number;
}

const indexCache = new Map<string, PlaylistGridIndex>();

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getStateRank(state: string): number {
  const rank = STATE_SORT_RANK[state];
  return rank == null ? Number.MAX_SAFE_INTEGER : rank;
}

function getDatasetSignature(playlists: PlaylistGridRow[]): string {
  const count = playlists.length;
  if (count === 0) return '0:0:0:0';

  const firstId = playlists[0]?.listId ?? 0;
  const lastId = playlists[count - 1]?.listId ?? 0;
  let maxUpdated = 0;

  for (const playlist of playlists) {
    if (playlist.updated > maxUpdated) {
      maxUpdated = playlist.updated;
    }
  }

  return `${count}:${maxUpdated}:${firstId}:${lastId}`;
}

function getOrderQueryKey(sort: SortState, search: SearchState): string {
  return `${search.mode}:${search.text.trim().toLowerCase()}:${sort.key}:${sort.direction}`;
}

function isSourceOrderUpdatedDesc(playlists: PlaylistGridRow[]): boolean {
  for (let index = 1; index < playlists.length; index += 1) {
    const prev = playlists[index - 1];
    const next = playlists[index];
    if (!prev || !next) {
      return false;
    }

    if (prev.updated < next.updated) {
      return false;
    }

    if (prev.updated === next.updated && prev.listId < next.listId) {
      return false;
    }
  }

  return true;
}

function touchIndex(cacheKey: string, index: PlaylistGridIndex): void {
  index.lastUsedAt = now();
  indexCache.delete(cacheKey);
  indexCache.set(cacheKey, index);

  while (indexCache.size > GRID_INDEX_CACHE_LIMIT) {
    const oldestKey = indexCache.keys().next().value;
    if (oldestKey == null) return;
    indexCache.delete(oldestKey);
  }
}

function touchSearchCache(cache: Map<string, Set<number>>, key: string, value: Set<number>): void {
  cache.delete(key);
  cache.set(key, value);

  while (cache.size > SEARCH_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey == null) return;
    cache.delete(oldestKey);
  }
}

function buildIndex(customerId: number | null, signature: string, playlists: PlaylistGridRow[]): PlaylistGridIndex {
  const idToPlaylist = new Map<number, PlaylistGridRow>();
  const sourceOrderIds: number[] = [];
  const records: DatasetRecord[] = [];

  for (const playlist of playlists) {
    const id = playlist.listId;
    idToPlaylist.set(id, playlist);
    sourceOrderIds.push(id);
    records.push({
      id,
      source: playlist,
      title: playlist.title,
      listType: playlist.listTypeLabel,
      state: playlist.state,
      created: playlist.created,
      updated: playlist.updated,
      lowerTitle: null,
      listTypeSortValue: null,
      stateRank: null,
    });
  }

  return {
    cacheKey: `${customerId ?? 0}:${signature}`,
    signature,
    idToPlaylist,
    sourceOrderIds,
    sourceOrderIsUpdatedDesc: isSourceOrderUpdatedDesc(playlists),
    records,
    sortOrders: {},
    exactSearchCache: new Map(),
    fuzzySearchCache: new Map(),
    fuzzyPreparedRecords: null,
    lastUsedAt: now(),
  };
}

function syncCachedIndex(index: PlaylistGridIndex, playlists: PlaylistGridRow[]): boolean {
  if (index.records.length !== playlists.length) {
    return false;
  }

  const nextById = new Map<number, PlaylistGridRow>();
  const nextSourceOrderIds: number[] = [];
  for (const playlist of playlists) {
    nextById.set(playlist.listId, playlist);
    nextSourceOrderIds.push(playlist.listId);
  }

  let needsSortReset = false;
  let needsSearchReset = false;

  for (const record of index.records) {
    const nextPlaylist = nextById.get(record.id);
    if (!nextPlaylist) {
      return false;
    }

    if (record.source === nextPlaylist) {
      continue;
    }

    const nextTitle = nextPlaylist.title;
    const nextListType = nextPlaylist.listTypeLabel;
    const nextState = nextPlaylist.state;
    const nextCreated = nextPlaylist.created;
    const nextUpdated = nextPlaylist.updated;

    if (
      record.title !== nextTitle ||
      record.listType !== nextListType ||
      record.state !== nextState ||
      record.created !== nextCreated ||
      record.updated !== nextUpdated
    ) {
      needsSortReset = true;
    }

    if (record.title !== nextTitle) {
      needsSearchReset = true;
    }

    record.source = nextPlaylist;
    record.title = nextTitle;
    record.listType = nextListType;
    record.state = nextState;
    record.created = nextCreated;
    record.updated = nextUpdated;
    record.lowerTitle = null;
    record.listTypeSortValue = null;
    record.stateRank = null;
  }

  index.sourceOrderIds = nextSourceOrderIds;
  index.sourceOrderIsUpdatedDesc = isSourceOrderUpdatedDesc(playlists);
  index.idToPlaylist = nextById;

  if (needsSortReset) {
    index.sortOrders = {};
  }

  if (needsSearchReset) {
    index.exactSearchCache.clear();
    index.fuzzySearchCache.clear();
    index.fuzzyPreparedRecords = null;
  }

  return true;
}

function getOrCreateIndex(customerId: number | null, playlists: PlaylistGridRow[]): PlaylistGridIndexResult {
  const signature = getDatasetSignature(playlists);
  const cacheKey = `${customerId ?? 0}:${signature}`;
  const cached = indexCache.get(cacheKey);

  if (cached) {
    const cacheIsValid = syncCachedIndex(cached, playlists);
    if (cacheIsValid) {
      touchIndex(cacheKey, cached);
      return { index: cached, cacheHit: true };
    }

    indexCache.delete(cacheKey);
  }

  const index = buildIndex(customerId, signature, playlists);
  touchIndex(cacheKey, index);
  return { index, cacheHit: false };
}

function getLowerTitle(record: DatasetRecord): string {
  if (record.lowerTitle !== null) {
    return record.lowerTitle;
  }

  record.lowerTitle = record.title.toLowerCase();
  return record.lowerTitle;
}

function getListTypeSortValue(record: DatasetRecord): string {
  if (record.listTypeSortValue !== null) {
    return record.listTypeSortValue;
  }

  record.listTypeSortValue = record.listType.toLowerCase();
  return record.listTypeSortValue;
}

function getRecordStateRank(record: DatasetRecord): number {
  if (record.stateRank !== null) {
    return record.stateRank;
  }

  record.stateRank = getStateRank(record.state);
  return record.stateRank;
}

function compareRecordAsc(a: DatasetRecord, b: DatasetRecord, key: PlaylistSortKey): number {
  switch (key) {
    case 'title': {
      const aTitle = getLowerTitle(a);
      const bTitle = getLowerTitle(b);
      if (aTitle < bTitle) return -1;
      if (aTitle > bTitle) return 1;
      return a.id - b.id;
    }
    case 'listType': {
      const aListType = getListTypeSortValue(a);
      const bListType = getListTypeSortValue(b);
      if (aListType < bListType) return -1;
      if (aListType > bListType) return 1;
      return a.id - b.id;
    }
    case 'state': {
      const aState = getRecordStateRank(a);
      const bState = getRecordStateRank(b);
      if (aState < bState) return -1;
      if (aState > bState) return 1;
      return a.id - b.id;
    }
    case 'created':
      if (a.created < b.created) return -1;
      if (a.created > b.created) return 1;
      return a.id - b.id;
    case 'updated':
      if (a.updated < b.updated) return -1;
      if (a.updated > b.updated) return 1;
      return a.id - b.id;
    default:
      return a.id - b.id;
  }
}

function ensureSortOrder(index: PlaylistGridIndex, key: PlaylistSortKey): { order: SortOrder; buildMs: number } {
  const cached = index.sortOrders[key];
  if (cached) {
    return { order: cached, buildMs: 0 };
  }

  const buildStart = now();
  const ascRecords = [...index.records].sort((a, b) => compareRecordAsc(a, b, key));
  const asc = ascRecords.map((record) => record.id);
  const order: SortOrder = {
    asc,
    desc: [...asc].reverse(),
  };

  index.sortOrders[key] = order;
  return {
    order,
    buildMs: now() - buildStart,
  };
}

function ensureFuzzyPreparedRecords(index: PlaylistGridIndex): FuzzyPreparedRecord[] {
  if (index.fuzzyPreparedRecords) {
    return index.fuzzyPreparedRecords;
  }

  index.fuzzyPreparedRecords = index.records.map((record) => ({
    id: record.id,
    preparedTitle: fuzzysort.prepare(record.title),
  }));

  return index.fuzzyPreparedRecords;
}

function getExactMatchSet(index: PlaylistGridIndex, queryLower: string): Set<number> {
  const cached = index.exactSearchCache.get(queryLower);
  if (cached) {
    touchSearchCache(index.exactSearchCache, queryLower, cached);
    return cached;
  }

  const matched = new Set<number>();
  for (const record of index.records) {
    if (getLowerTitle(record).includes(queryLower)) {
      matched.add(record.id);
    }
  }

  touchSearchCache(index.exactSearchCache, queryLower, matched);
  return matched;
}

function getFuzzyMatchSet(index: PlaylistGridIndex, query: string): Set<number> {
  const queryKey = query.trim().toLowerCase();
  const cached = index.fuzzySearchCache.get(queryKey);
  if (cached) {
    touchSearchCache(index.fuzzySearchCache, queryKey, cached);
    return cached;
  }

  const results = fuzzysort.go(query, ensureFuzzyPreparedRecords(index), {
    key: 'preparedTitle',
    threshold: -10000,
    limit: Infinity,
  });

  const matched = new Set<number>();
  for (const result of results) {
    matched.add(result.obj.id);
  }

  touchSearchCache(index.fuzzySearchCache, queryKey, matched);
  return matched;
}

function filterOrder(order: number[], allowed: Set<number>): number[] {
  if (allowed.size === order.length) {
    return order;
  }

  const filtered: number[] = [];
  for (const id of order) {
    if (allowed.has(id)) {
      filtered.push(id);
    }
  }

  return filtered;
}

function computeOrderedIdsMain(index: PlaylistGridIndex, search: SearchState, sort: SortState): ComputeResult {
  const totalStart = now();
  const query = search.text.trim();

  const searchStart = now();
  let matchedIds: Set<number> | null = null;
  if (query.length > 0) {
    matchedIds = search.mode === 'exact'
      ? getExactMatchSet(index, query.toLowerCase())
      : getFuzzyMatchSet(index, query);
  }
  const searchMs = now() - searchStart;

  const sortStart = now();
  let sortBuildMs = 0;
  let baseOrder: number[];

  if (sort.key === 'updated' && sort.direction === 'desc' && index.sourceOrderIsUpdatedDesc) {
    baseOrder = index.sourceOrderIds;
  } else {
    const ensuredOrder = ensureSortOrder(index, sort.key);
    sortBuildMs = ensuredOrder.buildMs;
    baseOrder = sort.direction === 'asc' ? ensuredOrder.order.asc : ensuredOrder.order.desc;
  }

  const ids = matchedIds ? filterOrder(baseOrder, matchedIds) : baseOrder;
  const sortMs = now() - sortStart;

  return {
    ids,
    timings: {
      searchMs,
      sortMs,
      sortBuildMs,
      totalMs: now() - totalStart,
    },
  };
}

function reportOrderTimings(timings: TimingStats, meta: Record<string, unknown>): void {
  recordDuration('playlists.search.compute', timings.searchMs, meta);
  recordDuration('playlists.sort.compute', timings.sortMs, meta);
  recordDuration('playlists.engine.orderCompute', timings.totalMs, meta);
}

export function usePlaylistGridEngine({
  customerId,
  playlists,
  sort,
  search,
}: UsePlaylistGridEngineArgs): UsePlaylistGridEngineResult {
  const setOrderedIdsSnapshot = usePlaylistGridState((state) => state.setOrderedIdsSnapshot);
  const trimmedSearchText = search.text.trim();
  const orderQueryKey = useMemo(
    () => getOrderQueryKey(sort, search),
    [sort.key, sort.direction, search.mode, search.text]
  );

  const indexedDataset = useMemo(() => {
    const start = now();
    const result = getOrCreateIndex(customerId, playlists);

    recordDuration('playlists.engine.datasetBuild', now() - start, {
      count: playlists.length,
      cacheHit: result.cacheHit,
      signature: result.index.signature,
      path: 'main',
    });

    return result;
  }, [customerId, playlists]);

  const index = indexedDataset.index;

  useEffect(() => {
    recordSample('playlists.engine.path', 0, {
      mode: search.mode,
      queryLength: trimmedSearchText.length,
      count: playlists.length,
      key: sort.key,
      direction: sort.direction,
      path: 'main',
    });
  }, [search.mode, trimmedSearchText.length, playlists.length, sort.key, sort.direction]);

  const mainResult = useMemo(() => {
    const computed = computeOrderedIdsMain(index, search, sort);

    reportOrderTimings(computed.timings, {
      path: 'main',
      mode: search.mode,
      queryLength: trimmedSearchText.length,
      resultCount: computed.ids.length,
      key: sort.key,
      direction: sort.direction,
    });

    return computed;
  }, [index, search.mode, search.text, sort.key, sort.direction, trimmedSearchText.length]);

  const orderedIds = mainResult.ids;

  useEffect(() => {
    setOrderedIdsSnapshot(index.signature, orderQueryKey, orderedIds);
  }, [orderedIds, orderQueryKey, index.signature, setOrderedIdsSnapshot]);

  const getIdByIndex = useCallback(
    (indexValue: number) => orderedIds[indexValue],
    [orderedIds]
  );

  const getRowByIndex = useCallback(
    (indexValue: number): PlaylistGridRow | null => {
      const id = orderedIds[indexValue];
      if (id == null) return null;
      return index.idToPlaylist.get(id) ?? null;
    },
    [orderedIds, index.idToPlaylist]
  );

  return {
    count: orderedIds.length,
    orderedIds,
    getIdByIndex,
    getRowByIndex,
    resultCount: orderedIds.length,
  };
}
